import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { useInView } from '@/hooks/useInView';

export default function CategoryGrid() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [sectionRef, isInView] = useInView();

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    return () => el.removeEventListener('scroll', checkScroll);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = 340;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  return (
    <section ref={sectionRef} className="relative bg-background-100 py-16 md:py-20">
      <div className={`max-w-[1400px] mx-auto px-6 md:px-10 transition-all duration-700 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-label text-sm font-medium text-foreground-400 uppercase tracking-wider mb-2">
              Shop by
            </p>
            <h2 className="font-heading text-3xl md:text-4xl italic font-semibold text-foreground-950">
              Category
            </h2>
          </div>

          <div className="flex items-center gap-8">
            <p className="text-sm text-foreground-500 leading-relaxed max-w-md hidden lg:block">
              Browse through our complete <strong>mulebuy spreadsheet</strong> category system. Every category links directly to verified product listings.
            </p>
            <span className="hidden md:inline-flex items-center px-4 py-1.5 rounded-full bg-foreground-900 text-background-50 text-xs font-semibold whitespace-nowrap">
              {categories.length} Categories
            </span>
          </div>
        </div>

        <div className="relative">
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background-50 border border-background-200/70 flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer shadow-sm"
            >
              <i className="ri-arrow-left-s-line text-lg text-foreground-700"></i>
            </button>
          )}

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-6 -mx-2 px-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {categories.map((cat, idx) => (
              <a
                key={cat.slug}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex-shrink-0 w-[280px] bg-background-50 rounded-2xl overflow-hidden border border-background-200/50 hover:border-accent-300 transition-all group cursor-pointer"
                style={{ transitionDelay: `${idx * 40}ms` }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={cat.imageUrl}
                    alt={`${cat.name} category from mulebuy spreadsheet`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <span className="inline-block px-3 py-1.5 rounded-lg bg-background-50/90 backdrop-blur-sm text-foreground-900 text-xs font-semibold whitespace-nowrap">
                      {cat.name}
                    </span>
                  </div>
                </div>
                <div className="p-4 flex items-center justify-between">
                  <p className="text-xs text-foreground-500">Browse verified listings</p>
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 group-hover:bg-accent-100 transition-colors">
                    <i className="ri-arrow-right-up-line text-sm text-foreground-500 group-hover:text-accent-600 transition-colors"></i>
                  </span>
                </div>
              </a>
            ))}
          </div>

          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background-50 border border-background-200/70 flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer shadow-sm"
            >
              <i className="ri-arrow-right-s-line text-lg text-foreground-700"></i>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}